/**
 * Mantenimiento Traspaso — migración web de ares6060.
 *
 * Lista los traspasos vigentes de la tabla `traspaso` (por cédula o rango de
 * fechas, excluyentes) y permite anularlos (valor=0) o imprimir el
 * comprobante "Cobro de Traspaso" con los datos del destinatario.
 * Cancelar requiere el permiso `borrar` de ares6060 (lo valida el backend).
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { encabezadoEmpresaHtml } from "lib/impresionhtml";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin traspasos vigentes en el rango
      </Typography>
    </Stack>
  );
}

// Comprobante "Cobro de Traspaso" (mismo contenido que ares6060.imprimirtraspaso).
function imprimirTraspaso(row, compania) {
  const fila = (k, v) =>
    `<tr><td style="padding:3px 8px;color:#555">${k}</td><td style="padding:3px 8px;font-weight:bold">${
      v || ""
    }</td></tr>`;
  const html = `
    <html><head><meta name="color-scheme" content="light"><title>Cobro de Traspaso</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:20px;font-size:13px;max-width:520px;margin:0 auto">
      ${encabezadoEmpresaHtml(compania)}
      <p style="text-align:center;margin:0 0 4px;font-weight:bold">Cobro de Traspaso</p>
      <p style="text-align:center;margin:0 0 14px;font-size:11px">A la fecha ${new Date().toLocaleDateString(
        "es-DO"
      )}</p>
      <p style="margin:10px 0 4px;font-weight:bold;border-bottom:1px solid #000">DATOS DEL TRASPASO</p>
      <table style="width:100%;border-collapse:collapse">
        ${fila("Chasis", row.chasis)}
        ${fila("Marca", row.marca)}
        ${fila("Modelo", row.modelo)}
        ${fila("Color", row.colore)}
      </table>
      <p style="margin:14px 0 4px;font-weight:bold;border-bottom:1px solid #000">TRASPASAR A:</p>
      <table style="width:100%;border-collapse:collapse">
        ${fila("Cédula", row.tcedula)}
        ${fila("Nombres", [row.tnombres, row.tapellidos].filter(Boolean).join(" "))}
        ${fila("Valor", "RD$ " + fmtMoney(row.valor))}
      </table>
      <p style="margin-top:40px;border-top:1px solid #000;padding-top:6px;text-align:center;width:60%;margin-left:auto;margin-right:auto">Revisado Por</p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

export default function CancelarTraspaso({ embedded = false, refreshToken = 0 } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [criterio, setCriterio] = useState("");
  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [rows, setRows] = useState([]);
  const [compania, setCompania] = useState("");
  const [loading, setLoading] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/traspaso_listar", {
        criterio: criterio.trim(),
        fechadesde: fecha1,
        fechahasta: fecha2,
      });
      if (ok) {
        setRows(data.traspasos || []);
        setCompania(data.compania || "");
      } else {
        showAlert({ message: data.Error || "No se pudieron listar los traspasos", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  // ---- Cancelación ----
  const [target, setTarget] = useState(null);
  const [cancelando, setCancelando] = useState(false);

  const cancelar = async () => {
    setCancelando(true);
    try {
      const { ok, data } = await post("/api/traspaso_cancelar", { id: target.id });
      if (ok) {
        showAlert({ message: data.mensajeok || "Traspaso cancelado", color: "success" });
        setTarget(null);
        cargar();
      } else {
        showAlert({ message: data.Error || "No se pudo cancelar", color: "error" });
      }
    } finally {
      setCancelando(false);
    }
  };

  const columns = [
    { field: "noprest", headerName: "Préstamo", width: 95 },
    { field: "fecha", headerName: "Fecha", width: 105 },
    { field: "cedula", headerName: "Cédula", width: 135 },
    { field: "nombres", headerName: "Cliente", flex: 1, minWidth: 160 },
    { field: "chasis", headerName: "Chasis", width: 170 },
    {
      field: "destinatario",
      headerName: "Traspasar a",
      flex: 1,
      minWidth: 160,
      valueGetter: (_v, row) => [row.tnombres, row.tapellidos].filter(Boolean).join(" "),
    },
    {
      field: "valor",
      headerName: "Valor",
      width: 115,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "acciones",
      headerName: "",
      width: 230,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.8}>
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setTarget(params.row)}
            sx={btnOutline(FP.color.danger)}
          >
            Cancelar
          </Button>
          <Button
            size="small"
            startIcon={<PrintOutlinedIcon />}
            onClick={() => imprimirTraspaso(params.row, compania)}
            sx={btnOutline(FP.color.navy)}
          >
            Imprimir
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box component={embedded ? "section" : "main"} sx={embedded ? {} : { minHeight: "100vh", bgcolor: FP.color.surface }}>
      {!embedded && (
        <PageHeader
          title="Mantenimiento Traspaso"
          subtitle="Anula traspasos de matrícula vigentes e imprime el comprobante — requiere permiso de borrado"
        />
      )}
      <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Buscar por cédula"
                placeholder="Dejar vacío para buscar por fechas"
                value={criterio}
                onChange={(e) => setCriterio(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                disabled={!!criterio.trim()}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                disabled={!!criterio.trim()}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={2} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={cargar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
            <Grid item xs={6} sm={12} md={3} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Typography
                sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}
              >
                {rows.length} traspasos
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              rowHeight={46}
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      {/* Confirmación */}
      <Dialog
        open={!!target}
        onClose={() => setTarget(null)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.danger }}>
          Cancelar traspaso
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Cancelar el traspaso del chasis <b>{target?.chasis}</b> a{" "}
            <b>{[target?.tnombres, target?.tapellidos].filter(Boolean).join(" ")}</b> por RD${" "}
            <b>{fmtMoney(target?.valor)}</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTarget(null)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              cancelando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
            }
            onClick={cancelar}
            disabled={cancelando}
            sx={btnSolid("#D32F2F", "#B71C1C")}
          >
            Sí, cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
